import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { NEW_API_SERVER } from "../../components/Auth/AuthGuard";
import {
    ApartmentOutlined,
    ArrowLeftOutlined,
    CheckCircleFilled,
    ClockCircleFilled,
    ClusterOutlined,
    ColumnWidthOutlined,
    DatabaseOutlined,
    DotChartOutlined,
    ExperimentOutlined,
    FileTextOutlined,
    FileWordOutlined,
    HistoryOutlined,
    InboxOutlined,
    LineChartOutlined,
    ReconciliationOutlined,
    RightOutlined,
    SearchOutlined,
    SyncOutlined,
    ThunderboltOutlined,
} from "@ant-design/icons";


const CAPABILITIES = [
    {
        key: "survey-free",
        icon: <SearchOutlined />,
        tone: "violet",
        title: "بدون نیاز به نظرسنجی",
        text: "بررسی مفاهیم ذهنی افراد جامعه بدون برگزاری نظرسنجی و پرسشنامه.",
    },
    {
        key: "distance",
        icon: <ColumnWidthOutlined />,
        tone: "indigo",
        title: "فاصله معنایی به فاصله هندسی",
        text: "تبدیل مفهوم استعاری فاصله بین مفاهیم ذهنی به فاصله هندسی قابل اندازه‌گیری.",
    },
    {
        key: "hot-topics",
        icon: <ClusterOutlined />,
        tone: "cyan",
        title: "موضوعات داغ و روابط پنهان",
        text: "بررسی موضوعات مطرح‌شده در جوامع کاربری و استخراج روابط پنهان میان آن‌ها.",
    },
    {
        key: "time-travel",
        icon: <HistoryOutlined />,
        tone: "amber",
        title: "نگاه به گذشته",
        text: "تحلیل ذهنیت افراد درباره یک مفهوم در گذشته، بدون محدودیت زمان نظرسنجی.",
    },
];

const QUICK_TOOLS = [
    {
        key: "non-metric",
        to: "/ReviewNonMetricSpace?tab=0",
        icon: <DotChartOutlined />,
        tone: "violet",
        title: "نمایش هندسی واژگان",
        text: "ابر واژگان و فضای بردار واژگان",
    },
    {
        key: "concepts",
        to: "/ReviewDataAndDefineConcepts?tab=0",
        icon: <LineChartOutlined />,
        tone: "indigo",
        title: "فاصله بین مفاهیم",
        text: "همسایگی واژگان و سنجش فاصله",
    },
    {
        key: "knowledge",
        to: "/ProportionalityBetweenConcepts?tab=0",
        icon: <ReconciliationOutlined />,
        tone: "cyan",
        title: "استخراج دانش",
        text: "نسبت‌سنجی و پیشنهاد واژه",
    },
    {
        key: "semantic",
        to: "/LexicalSemantic",
        icon: <ExperimentOutlined />,
        tone: "emerald",
        title: "تحلیل معنایی واژگان",
        text: "استخراج وجوه و خوشه‌های معنایی",
    },
    {
        key: "community",
        to: "/CommunityAnalysis",
        icon: <ApartmentOutlined />,
        tone: "rose",
        title: "تحلیل جوامع کاربری",
        text: "شناسایی جوامع و تحلیل موضوعی",
    },
    {
        key: "words",
        to: "/WordsSuggestion",
        icon: <FileWordOutlined />,
        tone: "amber",
        title: "پیشنهاد واژگان",
        text: "واژگان مناسب برای تولید پیام",
    },
];


const faNum = (n) => {
    if (n === null || n === undefined || n === "") return "۰";
    const num = Number(n);
    return Number.isNaN(num) ? String(n) : num.toLocaleString("fa-IR");
};

const faDate = (value) => {
    if (!value) return "—";
    try {
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return "—";
        return parsed.toLocaleDateString("fa-IR", { year: "numeric", month: "short", day: "numeric" });
    } catch { return "—"; }
};

const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 5) return "شب بخیر";
    if (hour < 12) return "صبح بخیر";
    if (hour < 17) return "وقت بخیر";
    return "عصر بخیر";
};


const CogSpaceDashboard = () => {
    const navigate = useNavigate();
    const account = useSelector((state) => state.account);
    const token = account?.token;

    const [itemsCount, setItemsCount] = useState(null);
    const [lastRequests, setLastRequests] = useState(null);
    const [loading, setLoading] = useState(false);

    const displayName = useMemo(() => {
        const user = account?.user || account?.currentUser || account?.profile || account || {};
        const combined = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
        return user?.full_name || combined || user?.username || user?.name || "کاربر گرامی";
    }, [account]);

    const loadDashboard = useCallback(
        async ({ silent = false } = {}) => {
            if (!token) return;
            if (!silent) setLoading(true);
            const config = { headers: { Authorization: `${token}` } };

            const [countRes, requestsRes] = await Promise.allSettled([
                axios.get(`${NEW_API_SERVER()}common/get-user-requests-count`, config),
                axios.get(`${NEW_API_SERVER()}common/get-4-last-user-requests`, config),
            ]);

            if (countRes.status === "fulfilled") setItemsCount(countRes.value.data?.count ?? 0);
            if (requestsRes.status === "fulfilled") {
                const requests = requestsRes.value.data?.requests;
                setLastRequests(Array.isArray(requests) ? requests : Object.values(requests || {}));
            }

            if (countRes.status === "rejected" && requestsRes.status === "rejected") {
                toast.error("دریافت اطلاعات داشبورد ناموفق بود");
            }
            if (!silent) setLoading(false);
        },
        [token]
    );

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const answered = useMemo(
        () => (lastRequests || []).filter((r) => r?.result).length,
        [lastRequests]
    );

    const refresh = async () => {
        const toastId = toast.loading("در حال بروزرسانی...");
        await loadDashboard({ silent: true });
        toast.success("اطلاعات بروزرسانی شد", { id: toastId });
    };

    return (
        <div className="cs-root">


            <section className="cs-hero cs-rise">
                <div className="cs-hero-glow" />
                <div className="cs-hero-body">
                    <span className="cs-hero-badge">
                        <ThunderboltOutlined /> سامانه پایش فضای شناختی
                    </span>
                    <h1 className="cs-hero-title">
                        {greeting()}، {displayName}
                    </h1>
                    <p className="cs-hero-text">
                        سامانه پایش فضای شناختی یک پروژه دانش‌بنیان در زمینه مدل‌سازی فضای شناختی ذهنیت افراد
                        جامعه است. ادراک افراد از مفاهیم در باورها و عقاید آن‌ها ریشه دارد و با تجربیات روزمره
                        جمعی تحت تاثیر قرار می‌گیرد. اطلاعات ارائه‌شده در این سامانه، مدلی محاسباتی است که نه‌تنها
                        آگاهی آشکار افراد، بلکه تاثیر آگاهی ضعیف آن‌ها در درک یک مفهوم را نیز بازنمایی می‌کند.
                    </p>
                    <div className="cs-hero-actions">
                        <button className="cs-btn cs-btn--light" onClick={() => navigate("/ReviewNonMetricSpace?tab=0")}>
                            <DotChartOutlined /> شروع تحلیل فضای واژگان
                        </button>
                        <button className="cs-btn cs-btn--ghost" onClick={() => navigate("/ModelTraining")}>
                            <DatabaseOutlined /> آموزش مدل‌ها
                        </button>
                    </div>
                </div>

                <div className="cs-hero-stats">
                    <div className="cs-hstat">
                        <span className="cs-hstat-icon"><InboxOutlined /></span>
                        <span className="cs-hstat-body">
                            <b>{itemsCount === null ? "—" : faNum(itemsCount)}</b>
                            <small>کل درخواست‌های تامین داده</small>
                        </span>
                    </div>
                    <div className="cs-hstat">
                        <span className="cs-hstat-icon cs-hstat-icon--green"><CheckCircleFilled /></span>
                        <span className="cs-hstat-body">
                            <b>{lastRequests === null ? "—" : faNum(answered)}</b>
                            <small>پاسخ داده‌شده از ۴ مورد اخیر</small>
                        </span>
                    </div>
                </div>
            </section>


            <section className="cs-block cs-rise" style={{ animationDelay: ".05s" }}>
                <div className="cs-block-head">
                    <span className="cs-block-title">
                        <ClusterOutlined /> مهم‌ترین کاربردهای سامانه
                    </span>
                </div>
                <div className="cs-cap-grid">
                    {CAPABILITIES.map((item) => (
                        <article key={item.key} className="cs-cap">
                            <span className={`cs-cap-icon cs-tone--${item.tone}`}>{item.icon}</span>
                            <h3 className="cs-cap-title">{item.title}</h3>
                            <p className="cs-cap-text">{item.text}</p>
                        </article>
                    ))}
                </div>
            </section>


            <div className="cs-columns">
                <section className="cs-block cs-rise" style={{ animationDelay: ".1s" }}>
                    <div className="cs-block-head">
                        <span className="cs-block-title">
                            <ThunderboltOutlined /> دسترسی سریع به ابزارها
                        </span>
                    </div>
                    <div className="cs-tool-grid">
                        {QUICK_TOOLS.map((tool) => (
                            <button
                                key={tool.key}
                                className="cs-tool"
                                onClick={() => navigate(tool.to)}
                            >
                                <span className={`cs-tool-icon cs-tone--${tool.tone}`}>{tool.icon}</span>
                                <span className="cs-tool-copy">
                                    <strong>{tool.title}</strong>
                                    <small>{tool.text}</small>
                                </span>
                                <ArrowLeftOutlined className="cs-tool-arrow" />
                            </button>
                        ))}
                    </div>
                </section>

                <section className="cs-block cs-rise" style={{ animationDelay: ".15s" }}>
                    <div className="cs-block-head">
                        <span className="cs-block-title">
                            <HistoryOutlined /> آخرین درخواست‌های تامین داده
                        </span>
                        <div className="cs-block-actions">
                            <button className="cs-icon-btn" onClick={refresh} title="بروزرسانی">
                                <SyncOutlined spin={loading} />
                            </button>
                            <button className="cs-link-btn" onClick={() => navigate("/userrequest")}>
                                همه درخواست‌ها
                                {itemsCount !== null && <span className="cs-count">{faNum(itemsCount)}</span>}
                                <RightOutlined />
                            </button>
                        </div>
                    </div>

                    {lastRequests === null ? (
                        <div className="cs-skeletons">
                            {[0, 1, 2].map((i) => <div key={i} className="cs-skeleton" />)}
                        </div>
                    ) : lastRequests.length === 0 ? (
                        <div className="cs-empty">
                            <span className="cs-empty-icon"><InboxOutlined /></span>
                            <strong>درخواستی ثبت نشده است</strong>
                            <small>پس از ثبت اولین درخواست تامین داده، اینجا نمایش داده می‌شود</small>
                        </div>
                    ) : (
                        <div className="cs-req-list">
                            {lastRequests.map((item, idx) => (
                                <button
                                    key={item?.id ?? idx}
                                    className="cs-req"
                                    onClick={() => navigate(`/userrequest?id=${item?.id}`)}
                                >
                                    <span className={`cs-req-status ${item?.result ? "is-done" : "is-pending"}`}>
                                        {item?.result ? <CheckCircleFilled /> : <ClockCircleFilled />}
                                    </span>
                                    <span className="cs-req-body">
                                        <span className="cs-req-head">
                                            <strong>{item?.data?.title || "بدون عنوان"}</strong>
                                            {item?.type && <span className="cs-req-tag">{item.type}</span>}
                                        </span>
                                        {item?.data?.desc && <span className="cs-req-desc">{item.data.desc}</span>}
                                        <span className="cs-req-meta">
                                            <FileTextOutlined />
                                            بازه: {faDate(item?.data?.start_time)} تا {faDate(item?.data?.end_time)}
                                        </span>
                                    </span>
                                    <ArrowLeftOutlined className="cs-req-arrow" />
                                </button>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            <style>{`
                .cs-root {
                    direction: rtl;
                    display: flex; flex-direction: column; gap: 20px;
                    font-family: inherit;
                }
                .cs-rise { animation: cs-rise .5s cubic-bezier(.4,0,.2,1) both; }
                @keyframes cs-rise {
                    from { opacity: 0; transform: translateY(16px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

            
                .cs-hero {
                    position: relative; overflow: hidden;
                    display: flex; gap: 26px; flex-wrap: wrap; align-items: stretch;
                    padding: 28px 30px;
                    border-radius: 26px;
                    background: linear-gradient(125deg, #4c1d95 0%, #4338ca 48%, #1e1b4b 100%);
                    box-shadow: 0 18px 44px rgba(49,46,129,.28);
                }
                .cs-hero-glow {
                    position: absolute; top: -120px; left: -80px;
                    width: 340px; height: 340px; border-radius: 50%;
                    background: radial-gradient(circle, rgba(167,139,250,.45), transparent 65%);
                    pointer-events: none;
                }
                .cs-hero-body { position: relative; flex: 1 1 460px; min-width: 0; }
                .cs-hero-badge {
                    display: inline-flex; align-items: center; gap: 7px;
                    background: rgba(255,255,255,.14);
                    border: 1px solid rgba(255,255,255,.22);
                    color: #ddd6fe; border-radius: 20px;
                    padding: 5px 14px; font-size: 11.5px; font-weight: 800;
                }
                .cs-hero-title {
                    margin: 14px 0 10px; color: #fff;
                    font-size: 24px; font-weight: 800; line-height: 1.6;
                }
                .cs-hero-text {
                    margin: 0; color: rgba(224,231,255,.82);
                    font-size: 13.5px; line-height: 2.2; text-align: justify;
                    max-width: 760px;
                }
                .cs-hero-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 20px; }

                .cs-btn {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 11px 20px; border-radius: 13px;
                    font-family: inherit; font-size: 13px; font-weight: 800;
                    cursor: pointer; transition: all .2s; border: 1px solid transparent;
                }
                .cs-btn:hover { transform: translateY(-2px); }
                .cs-btn--light {
                    background: #fff; color: #4338ca;
                    box-shadow: 0 8px 22px rgba(0,0,0,.18);
                }
                .cs-btn--light:hover { background: #f5f3ff; box-shadow: 0 12px 28px rgba(0,0,0,.24); }
                .cs-btn--ghost {
                    background: rgba(255,255,255,.12); color: #fff;
                    border-color: rgba(255,255,255,.28);
                }
                .cs-btn--ghost:hover { background: rgba(255,255,255,.22); }

                .cs-hero-stats {
                    position: relative;
                    flex: 0 1 250px;
                    display: flex; flex-direction: column; gap: 12px; justify-content: center;
                }
                .cs-hstat {
                    display: flex; align-items: center; gap: 12px;
                    background: rgba(255,255,255,.1);
                    border: 1px solid rgba(255,255,255,.18);
                    border-radius: 18px; padding: 14px 16px;
                    backdrop-filter: blur(4px);
                }
                .cs-hstat-icon {
                    width: 42px; height: 42px; flex-shrink: 0; border-radius: 13px;
                    display: grid; place-items: center; font-size: 18px; color: #fff;
                    background: linear-gradient(135deg, #7c3aed, #6366f1);
                }
                .cs-hstat-icon--green { background: linear-gradient(135deg, #10b981, #047857); }
                .cs-hstat-body { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
                .cs-hstat-body b { color: #fff; font-size: 22px; font-weight: 800; line-height: 1.4; }
                .cs-hstat-body small { color: rgba(199,210,254,.85); font-size: 11px; font-weight: 600; }

                
                .cs-block {
                    background: #fff; border: 1.5px solid #eceefb;
                    border-radius: 22px; padding: 20px 22px;
                    box-shadow: 0 4px 20px rgba(30,40,100,.06);
                }
                .cs-block-head {
                    display: flex; align-items: center; justify-content: space-between;
                    gap: 12px; flex-wrap: wrap; margin-bottom: 16px;
                }
                .cs-block-title {
                    display: inline-flex; align-items: center; gap: 8px;
                    font-size: 14px; font-weight: 800; color: #312e81;
                }
                .cs-block-title .anticon { color: #7c3aed; }
                .cs-block-actions { display: flex; align-items: center; gap: 8px; }
                .cs-icon-btn {
                    width: 34px; height: 34px; display: grid; place-items: center;
                    background: #eef2ff; color: #4338ca;
                    border: 1px solid #e0e7ff; border-radius: 11px;
                    cursor: pointer; font-size: 14px; transition: all .18s;
                }
                .cs-icon-btn:hover { background: #4f46e5; color: #fff; border-color: #4f46e5; }
                .cs-link-btn {
                    display: inline-flex; align-items: center; gap: 7px;
                    background: #eef2ff; color: #4338ca;
                    border: 1px solid #e0e7ff; border-radius: 20px;
                    padding: 7px 14px; font-size: 12px; font-weight: 800;
                    cursor: pointer; font-family: inherit; transition: all .18s;
                }
                .cs-link-btn:hover { background: #4f46e5; color: #fff; border-color: #4f46e5; }
                .cs-link-btn .anticon:last-child { font-size: 9px; }
                .cs-count {
                    background: rgba(79,70,229,.14); border-radius: 20px;
                    padding: 1px 8px; font-size: 11px;
                }
                .cs-link-btn:hover .cs-count { background: rgba(255,255,255,.24); }

                
                .cs-cap-grid {
                    display: grid; gap: 14px;
                    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
                }
                .cs-cap {
                    background: #fafbff; border: 1.5px solid #f0f2fb;
                    border-radius: 18px; padding: 18px 16px; transition: all .2s;
                }
                .cs-cap:hover {
                    border-color: #ddd6fe; transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(79,70,229,.12);
                }
                .cs-cap-icon {
                    width: 46px; height: 46px; border-radius: 15px;
                    display: grid; place-items: center;
                    font-size: 20px; color: #fff; margin-bottom: 12px;
                }
                .cs-cap-title { margin: 0 0 6px; font-size: 13.5px; font-weight: 800; color: #1e1b4b; }
                .cs-cap-text { margin: 0; font-size: 12.5px; line-height: 2; color: #6b7280; }

                
                .cs-tone--violet  { background: linear-gradient(135deg, #7c3aed, #4c1d95); box-shadow: 0 6px 16px rgba(124,58,237,.3); }
                .cs-tone--indigo  { background: linear-gradient(135deg, #4f46e5, #3730a3); box-shadow: 0 6px 16px rgba(79,70,229,.3); }
                .cs-tone--cyan    { background: linear-gradient(135deg, #0891b2, #0e7490); box-shadow: 0 6px 16px rgba(8,145,178,.3); }
                .cs-tone--amber   { background: linear-gradient(135deg, #f59e0b, #b45309); box-shadow: 0 6px 16px rgba(245,158,11,.3); }
                .cs-tone--emerald { background: linear-gradient(135deg, #10b981, #047857); box-shadow: 0 6px 16px rgba(16,185,129,.3); }
                .cs-tone--rose    { background: linear-gradient(135deg, #f43f5e, #9f1239); box-shadow: 0 6px 16px rgba(244,63,94,.3); }

               
                .cs-columns { display: grid; grid-template-columns: 1.15fr 1fr; gap: 20px; align-items: start; }

              
                .cs-tool-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
                .cs-tool {
                    display: flex; align-items: center; gap: 12px;
                    background: #fafbff; border: 1.5px solid #f0f2fb;
                    border-radius: 16px; padding: 13px 14px;
                    cursor: pointer; text-align: right; font-family: inherit;
                    transition: all .2s;
                }
                .cs-tool:hover {
                    background: #fff; border-color: #c7d2fe;
                    transform: translateY(-2px); box-shadow: 0 10px 24px rgba(79,70,229,.12);
                }
                .cs-tool-icon {
                    width: 40px; height: 40px; flex-shrink: 0; border-radius: 13px;
                    display: grid; place-items: center; font-size: 17px; color: #fff;
                }
                .cs-tool-copy { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
                .cs-tool-copy strong {
                    font-size: 12.5px; font-weight: 800; color: #1e1b4b;
                    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                }
                .cs-tool-copy small {
                    font-size: 11px; color: #9ca3af; font-weight: 600;
                    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                }
                .cs-tool-arrow { font-size: 11px; color: #c7d2fe; transition: all .2s; }
                .cs-tool:hover .cs-tool-arrow { color: #4f46e5; transform: translateX(-3px); }

               
                .cs-req-list { display: flex; flex-direction: column; gap: 10px; }
                .cs-req {
                    display: flex; align-items: flex-start; gap: 12px;
                    background: #fafbff; border: 1.5px solid #f0f2fb;
                    border-radius: 16px; padding: 13px 14px;
                    cursor: pointer; text-align: right; font-family: inherit;
                    transition: all .2s; width: 100%;
                }
                .cs-req:hover {
                    background: #fff; border-color: #c7d2fe;
                    transform: translateY(-2px); box-shadow: 0 10px 24px rgba(79,70,229,.1);
                }
                .cs-req-status {
                    width: 34px; height: 34px; flex-shrink: 0; border-radius: 11px;
                    display: grid; place-items: center; font-size: 15px;
                }
                .cs-req-status.is-done { background: #d1fae5; color: #059669; }
                .cs-req-status.is-pending { background: #fef3c7; color: #d97706; }
                .cs-req-body { display: flex; flex-direction: column; gap: 4px; min-width: 0; flex: 1; }
                .cs-req-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
                .cs-req-head strong { font-size: 13px; font-weight: 800; color: #1e1b4b; }
                .cs-req-tag {
                    background: #eef2ff; color: #4338ca; border: 1px solid #e0e7ff;
                    border-radius: 7px; padding: 2px 8px; font-size: 10.5px; font-weight: 700;
                }
                .cs-req-desc {
                    font-size: 12px; color: #6b7280; line-height: 1.9;
                    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
                }
                .cs-req-meta {
                    display: inline-flex; align-items: center; gap: 6px;
                    font-size: 11px; color: #9ca3af; font-weight: 600;
                }
                .cs-req-arrow { font-size: 11px; color: #c7d2fe; margin-top: 10px; transition: all .2s; }
                .cs-req:hover .cs-req-arrow { color: #4f46e5; transform: translateX(-3px); }

              
                .cs-empty {
                    display: flex; flex-direction: column; align-items: center; gap: 8px;
                    padding: 38px 18px; text-align: center;
                }
                .cs-empty-icon {
                    width: 62px; height: 62px; border-radius: 20px;
                    display: grid; place-items: center; font-size: 27px; color: #7c3aed;
                    background: linear-gradient(135deg, #f5f3ff, #ede9fe);
                    box-shadow: 0 8px 22px rgba(124,58,237,.14);
                    margin-bottom: 4px;
                }
                .cs-empty strong { font-size: 14.5px; font-weight: 800; color: #1e1b4b; }
                .cs-empty small { font-size: 12px; color: #9ca3af; max-width: 320px; line-height: 1.9; }

                .cs-skeletons { display: flex; flex-direction: column; gap: 10px; }
                .cs-skeleton {
                    height: 74px; border-radius: 16px;
                    background: linear-gradient(90deg, #f4f5fb 25%, #eceefb 50%, #f4f5fb 75%);
                    background-size: 200% 100%;
                    animation: cs-shimmer 1.4s ease-in-out infinite;
                }
                @keyframes cs-shimmer {
                    from { background-position: 200% 0; }
                    to   { background-position: -200% 0; }
                }

               
                @media (max-width: 1200px) {
                    .cs-columns { grid-template-columns: 1fr; }
                }
                @media (max-width: 720px) {
                    .cs-hero { padding: 22px 18px; border-radius: 20px; }
                    .cs-hero-title { font-size: 20px; }
                    .cs-hero-stats { flex: 1 1 100%; flex-direction: row; }
                    .cs-hstat { flex: 1; }
                    .cs-tool-grid { grid-template-columns: 1fr; }
                    .cs-block { padding: 16px; border-radius: 18px; }
                }
            `}</style>
        </div>
    );
};

export default CogSpaceDashboard;
